// Dummy data for the Visitor & Contractor module — RON-5466.
//
// The module is being built design-first: every screen renders from the fixtures here so the
// UI can be reviewed before the APIs exist. Endpoints will be supplied later; at that point
// `VisitorAPI.jsx` swaps its fixture reads for real `post(...)` calls and this file shrinks to
// test fixtures only.
//
// Values mirror `web-visitor-manager.html` (the RON-5466 prototype) so the React build and the
// signed-off prototype can be compared side by side.

// ── The single switch ────────────────────────────────────────────────────────────
// While true, the module reads entitlement and host rights from the mocks below instead of
// from real storage/API values. Flip to false once `HomeScreenBookings_api` carries the
// entitlement flags and RON-5463 exposes host rights — nothing else needs to change.
export const USE_VISITOR_MOCKS = true;

// ── Entitlement (tblcompany_property) ────────────────────────────────────────────
// Shape matches `getVisitorEntitlement()` in `src/utils/visitorEntitlement.js`.
export const MOCK_ENTITLEMENT = {
  visitorPortal: true,
  contractorPortal: true,
  maxSites: 4,
};

// ── Host invite rights (tblvisitor_host_permission, RON-5463) ────────────────────
// The prototype calls this `ME`. Five rights are granted per employee or per group:
//   canVisitor    may invite visitors
//   canContractor may invite vetted contractors
//   canParking    may assign a parking space to a guest
//   canDesk       may assign a desk to a guest
//   canReception  may open the reception dashboard
//
// `canReception` is read but deliberately unused for now — Reception is becoming a separate
// front-end shared by admin (CI-3) and member (React) on the Laravel API base, so this app
// should not link to it until that URL exists.
export const MOCK_HOST_PERMISSIONS = {
  canVisitor: true,
  canContractor: true,
  canParking: true,
  canDesk: true,
  canReception: true,
};

// ── Visits (tblvisitor_visit) ────────────────────────────────────────────────────
// Mirrors the prototype's INIT_VISITORS, but stores the DB enum values rather than the
// prototype's display strings, so screens need no change when the API is plugged in.
// `group` links visitors invited together in one submission — used by the group-cancel flow.
//
// TWO FIELDS HERE ARE DUMMY, with no backing anywhere yet. Both are rendered on the View screen
// because the prototype shows them, and both must be supplied by the real API before that screen
// is truthful:
//
//   itProvided  the prototype displays this but never captures it (hardcoded `it:'No'` on submit),
//               so there is no field on any form that feeds it
//   activity    `tblvisitor_visit` has no audit columns at all. The shape used here — event, when,
//               who — is the minimum the screen needs; edits will also need a field-level diff.
//               See docs/VisitorDetail.md.
//
// The trails below are written per visit so each row tells its own story (created / checked in /
// cancelled) rather than every visit showing one identical line.
export const MOCK_VISITS = [
  {
    id: "v-sarah",
    guid: "8f1c2a10-0001-4a3b-9c11-a1b2c3d4e001",
    bookingCode: "VIS-2026-0407-001",
    kind: "visitor",
    name: "Sarah Mitchell",
    email: "s.mitchell@acmecorp.com",
    company: "Acme Corp",
    startDate: "2026-04-07",
    endDate: "2026-04-07",
    arrivalTime: "09:30",
    expectedOut: "17:00",
    site: "Dublin HQ",
    building: "Block A",
    host: "Michael Walsh",
    type: "standard",
    status: "expected",
    group: "g-acme",
    // Dummy — see the note above this array.
    itProvided: true,
    activity: [
      { event: "created", at: "2026-08-20T09:12", by: "Michael Walsh" },
    ],
  },
  {
    id: "v-james",
    guid: "8f1c2a10-0002-4a3b-9c11-a1b2c3d4e002",
    bookingCode: "VIS-2026-0408-002",
    kind: "visitor",
    name: "James O'Brien",
    email: "j.obrien@vertexltd.com",
    company: "Vertex Ltd",
    startDate: "2026-04-08",
    endDate: "2026-04-09",
    arrivalTime: "11:00",
    expectedOut: "16:30",
    site: "London Office",
    building: null,
    host: "Michael Walsh",
    type: "vip",
    status: "expected",
    group: "g-acme",
    // Dummy — see the note above this array.
    itProvided: false,
    activity: [
      { event: "created", at: "2026-08-20T09:12", by: "Michael Walsh" },
    ],
  },
  {
    id: "v-priya",
    guid: "8f1c2a10-0003-4a3b-9c11-a1b2c3d4e003",
    bookingCode: "VIS-2026-0409-003",
    kind: "visitor",
    name: "Priya Sharma",
    email: "p.sharma@novatech.com",
    company: "Nova Tech",
    startDate: "2026-04-09",
    endDate: "2026-04-09",
    arrivalTime: "14:00",
    expectedOut: "18:00",
    site: "Dublin HQ",
    building: "Block B",
    host: "Michael Walsh",
    type: "standard",
    status: "expected",
    group: "g-acme",
    // Dummy — see the note above this array.
    itProvided: false,
    activity: [
      { event: "created", at: "2026-08-20T09:12", by: "Michael Walsh" },
    ],
  },
  {
    id: "v-declan",
    guid: "8f1c2a10-0004-4a3b-9c11-a1b2c3d4e004",
    bookingCode: "CON-2026-0410-004",
    kind: "contractor",
    name: "Declan Murphy",
    email: "declan@murphyelectrical.ie",
    company: "Murphy Electrical Ltd",
    trade: "Electrician",
    startDate: "2026-04-10",
    endDate: "2026-04-10",
    arrivalTime: "08:00",
    expectedOut: "17:00",
    site: "Dublin HQ",
    building: "Block A",
    host: "Michael Walsh",
    type: "standard",
    status: "expected",
    isProvisional: false,
    vettedUntil: "2026-12-31",
    group: "g-solo-declan",
    // Dummy — see the note above this array.
    itProvided: true,
    activity: [
      { event: "created", at: "2026-08-18T14:40", by: "Michael Walsh" },
    ],
  },
  {
    id: "v-aoife",
    guid: "8f1c2a10-0005-4a3b-9c11-a1b2c3d4e005",
    bookingCode: "CON-2026-0413-005",
    kind: "contractor",
    name: "Aoife Ryan",
    email: "aoife@ryanfacilities.ie",
    company: "Ryan Facilities",
    trade: "Facilities Manager",
    startDate: "2026-04-13",
    endDate: "2026-04-13",
    arrivalTime: "09:00",
    expectedOut: "15:00",
    site: "Dublin HQ",
    building: null,
    host: "Michael Walsh",
    type: "standard",
    status: "expected",
    isProvisional: true,
    vettedUntil: null,
    group: "g-solo-aoife",
    // Dummy — see the note above this array.
    itProvided: false,
    activity: [
      { event: "created", at: "2026-08-19T11:05", by: "Michael Walsh" },
    ],
  },
  {
    id: "v-tom",
    guid: "8f1c2a10-0006-4a3b-9c11-a1b2c3d4e006",
    bookingCode: "VIS-2026-0328-006",
    kind: "visitor",
    name: "Tom Gallagher",
    email: "t.gallagher@bluewave.io",
    company: "BlueWave",
    startDate: "2026-03-28",
    endDate: "2026-03-28",
    arrivalTime: "10:00",
    expectedOut: "12:00",
    site: "Dublin HQ",
    building: "Block A",
    host: "Michael Walsh",
    type: "standard",
    status: "cancelled",
    group: "g-solo-tom",
    // Dummy — see the note above this array.
    itProvided: false,
    activity: [
      { event: "created", at: "2026-03-20T10:30", by: "Michael Walsh" },
      { event: "cancelled", at: "2026-03-27T16:02", by: "Michael Walsh" },
    ],
  },
  {
    id: "v-lin",
    guid: "8f1c2a10-0007-4a3b-9c11-a1b2c3d4e007",
    bookingCode: "VIS-2026-0325-007",
    kind: "visitor",
    name: "Lin Wei",
    email: "l.wei@orbitsystems.com",
    company: "Orbit Systems",
    startDate: "2026-03-25",
    endDate: "2026-03-26",
    arrivalTime: "13:30",
    expectedOut: "17:30",
    site: "New York Office",
    building: "Tower",
    host: "Michael Walsh",
    type: "standard",
    status: "checked_in",
    group: "g-solo-lin",
    // Dummy — see the note above this array.
    itProvided: true,
    activity: [
      { event: "created", at: "2026-03-10T08:55", by: "Michael Walsh" },
      { event: "checked_in", at: "2026-03-25T13:34", by: "Reception" },
    ],
  },
  {
    id: "v-nuala",
    guid: "8f1c2a10-0008-4a3b-9c11-a1b2c3d4e008",
    bookingCode: "CON-2026-0320-008",
    kind: "contractor",
    name: "Nuala Brady",
    email: "nuala@apexhvac.ie",
    company: "Apex HVAC",
    trade: "HVAC engineer",
    startDate: "2026-03-20",
    endDate: "2026-03-20",
    arrivalTime: "08:30",
    expectedOut: "16:00",
    site: "Dublin HQ",
    building: "Block B",
    host: "Michael Walsh",
    type: "standard",
    status: "checked_out",
    isProvisional: false,
    vettedUntil: "2026-09-30",
    group: "g-solo-nuala",
    // Dummy — see the note above this array.
    itProvided: false,
    activity: [
      { event: "created", at: "2026-03-05T15:20", by: "Michael Walsh" },
      { event: "checked_in", at: "2026-03-20T08:28", by: "Reception" },
      { event: "checked_out", at: "2026-03-20T15:47", by: "Reception" },
    ],
  },
];

// ── Sites and buildings (tblvisitor_site / tblvisitor_building) ───────────────────
// Mirrors the prototype's SITE_GEO + SITE_BUILDINGS, restructured to match the real tables:
// a site owns 0..n buildings and exactly one is the default (§0.2). Sites with no buildings are
// deliberately represented too — the building picker must stay hidden for those.
//
// `lat`/`lng` are carried for the invite email map (§0), which arrives with the email work.
export const MOCK_SITES = [
  {
    id: "site-dublin",
    name: "Dublin HQ",
    address: "North Wall Quay, Dublin 1",
    timezone: "Europe/Dublin",
    lat: 53.3478,
    lng: -6.2297,
    buildings: [
      { id: "bld-block-a", name: "Block A", isDefault: true },
      { id: "bld-block-b", name: "Block B", isDefault: false },
    ],
  },
  {
    id: "site-london",
    name: "London Office",
    address: "10 Bishopsgate, London EC2",
    timezone: "Europe/London",
    lat: 51.5155,
    lng: -0.081,
    // No buildings: the picker must not render for this site.
    buildings: [],
  },
  {
    id: "site-newyork",
    name: "New York Office",
    address: "New York, NY",
    timezone: "America/New_York",
    lat: 40.7128,
    lng: -74.006,
    // Single building: picker stays hidden, but the visit still records it.
    buildings: [{ id: "bld-tower", name: "Tower", isDefault: true }],
  },
  {
    id: "site-berlin",
    name: "Berlin Office",
    address: "Berlin",
    timezone: "Europe/Berlin",
    lat: 52.52,
    lng: 13.405,
    buildings: [],
  },
];

// ── Contractor vetting register (Phase 4) ────────────────────────────────────────
// Contractors differ from visitors in one structural way: a visitor is ad-hoc and lives inline on
// the visit, whereas a contractor is a PERSON THE COMPANY HAS VETTED and therefore has a record of
// their own. The invite flow looks them up by email rather than typing their details in.
//
// Three vetting states, matching the epic:
//   cleared      approved, with a `vettedUntil` date they are approved up to
//   provisional  booked while approval is still in flight — allowed on site only once cleared
//   blocked      must not be invited at all
//
// `documents` is the compliance pack reception checks: insurance, safety training, method
// statements. Each carries its own expiry, which can lapse independently of the vetting itself —
// a contractor can be cleared until next June with insurance that ran out last week.
//
// Fixtures are dated around August 2026 so the date gate is exercisable: Declan is cleared well
// into next year, Fiona only until mid-September, so booking her past that date must fail.
export const CONTRACTOR_DB = [
  {
    id: "c-declan",
    email: "declan@murphyelectrical.ie",
    name: "Declan Murphy",
    company: "Murphy Electrical Ltd",
    trade: "Electrician",
    vettingState: "vetted",
    vettedUntil: "2027-06-30",
    documents: [
      { type: "insurance", name: "Public liability insurance", expiresOn: "2027-03-31" },
      { type: "safety", name: "Safe Pass card", expiresOn: "2028-01-14" },
      { type: "method", name: "Method statement — rewiring", expiresOn: null },
    ],
  },
  {
    id: "c-fiona",
    email: "fiona@keanewelding.ie",
    name: "Fiona Keane",
    company: "Keane Welding",
    trade: "Welder",
    // Cleared, but not for long — booking beyond this date must be refused.
    vettingState: "vetted",
    vettedUntil: "2026-09-15",
    documents: [
      { type: "insurance", name: "Public liability insurance", expiresOn: "2026-09-01" },
      { type: "safety", name: "Hot works certification", expiresOn: "2026-11-20" },
    ],
  },
  {
    id: "c-aoife",
    email: "aoife@ryanfacilities.ie",
    name: "Aoife Ryan",
    company: "Ryan Facilities",
    trade: "Facilities Manager",
    // Approval still in flight: bookable, but the visit is flagged provisional.
    vettingState: "waiting",
    vettedUntil: null,
    documents: [
      { type: "insurance", name: "Public liability insurance", expiresOn: "2027-01-31" },
    ],
  },
  {
    id: "c-brendan",
    email: "brendan@apexroofing.ie",
    name: "Brendan Cullen",
    company: "Apex Roofing",
    trade: "Roofer",
    vettingState: "expired",
    vettedUntil: null,
    blockedReason: "Site safety incident, March 2026. Contact facilities before re-inviting.",
    documents: [],
  },
  {
    id: "c-nuala",
    email: "nuala@apexhvac.ie",
    name: "Nuala Brady",
    company: "Apex HVAC",
    trade: "HVAC engineer",
    vettingState: "vetted",
    vettedUntil: "2026-09-30",
    documents: [
      // Already lapsed — the pack must show this even though the vetting itself is still valid.
      { type: "insurance", name: "Public liability insurance", expiresOn: "2026-07-31" },
      { type: "safety", name: "Safe Pass card", expiresOn: "2027-05-05" },
    ],
  },
];
